import React from 'react';
import { Typography, Box, IconButton, Stack, Button } from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

const iconMap = {
  github: GitHubIcon,
  linkedin: LinkedInIcon,
  email: EmailIcon,
  scholar: SchoolOutlinedIcon,
};

const Hero = ({ name, title, about, profileImage, social, cvUrl, layoutSideBySide }) => (
  <Box id="about" sx={{
    minHeight: { xs: 'auto', md: '70vh' },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    bgcolor: 'background.default',
    pt: { xs: 6, md: 12 },
    pb: { xs: 5, md: 10 },
    px: { xs: 2, md: 4 },
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 20% 50%, rgba(25, 118, 210, 0.03) 0%, transparent 50%)',
      pointerEvents: 'none',
    }
  }}>
    <Stack
      direction={{ xs: 'column', md: layoutSideBySide ? 'row' : 'column' }}
      spacing={{ xs: 4, md: layoutSideBySide ? 6 : 4 }}
      alignItems="center"
      justifyContent="center"
      sx={{ 
        width: '100%', 
        maxWidth: { xs: '100%', sm: '95%', md: 1400 }, 
        position: 'relative', 
        zIndex: 1,
        px: { xs: 2, sm: 3, md: 4 }
      }}
    >
      <Box
        component="img"
        src={profileImage}
        alt={name}
        sx={{
          width: { xs: 200, sm: 280, md: layoutSideBySide ? 360 : 280 },
          height: { xs: 200, sm: 280, md: layoutSideBySide ? 360 : 280 },
          objectFit: 'cover',
          borderRadius: '50%',
          mb: { xs: 2, md: 0 },
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
          border: '4px solid',
          borderColor: 'primary.main',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 6px 20px rgba(0, 0, 0, 0.1)',
          }
        }}
      />
      <Box sx={{ 
        textAlign: { xs: 'center', md: layoutSideBySide ? 'left' : 'center' }, 
        maxWidth: { xs: '100%', sm: '95%', md: layoutSideBySide ? 900 : 950 },
        width: '100%'
      }}>
        <Typography 
          variant="h2" 
          fontWeight={700} 
          gutterBottom 
          sx={{ 
            letterSpacing: '-0.02em',
            fontFamily: 'Montserrat, sans-serif', 
            fontSize: { xs: 28, sm: 36, md: 42 },
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1.5
          }}
        >
          {name}
        </Typography>
        {title && (
          <Typography 
            variant="h5" 
            fontWeight={500} 
            color="text.secondary" 
            gutterBottom 
            sx={{ 
              mb: 3.5, 
              fontFamily: 'Montserrat, sans-serif', 
              fontSize: { xs: 16, sm: 18, md: 20 },
              letterSpacing: '0.01em'
            }}
          >
            {title}
          </Typography>
        )}
        <Typography 
          variant="body1" 
          component="div"
          sx={{ 
            mb: 4, 
            color: 'text.primary', 
            fontSize: { xs: 15, sm: 16, md: 17 },
            lineHeight: 1.8,
            textAlign: { xs: 'left', md: layoutSideBySide ? 'left' : 'left' },
            maxWidth: '100%',
            '& p': {
              marginBottom: '1.2em',
              '&:last-child': {
                marginBottom: 0
              }
            },
            '& a': {
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 600,
              transition: 'all 0.2s ease-in-out',
              borderBottom: '1px solid transparent',
              '&:hover': {
                textDecoration: 'none',
                borderBottomColor: 'primary.main',
                color: 'primary.dark',
              }
            }
          }}
          dangerouslySetInnerHTML={{ __html: about }}
        />
        <Stack 
          direction="row" 
          spacing={1.5} 
          justifyContent={{ xs: 'center', md: layoutSideBySide ? 'flex-start' : 'center' }} 
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          sx={{ mt: 1 }}
        >
          {social.map(({ icon, url }) => {
            const Icon = iconMap[icon];
            return (
              <IconButton 
                color="primary" 
                href={url} 
                target="_blank" 
                key={icon} 
                size="large"
                sx={{ 
                  border: '2px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'transparent',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  },
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '1.4rem', sm: '1.6rem' }
                  }
                }}
              >
                <Icon fontSize="inherit" />
              </IconButton>
            );
          })}
          {cvUrl && (
            <Button
              variant="outlined"
              color="primary"
              href={cvUrl}
              download="Parvin_Malekzadeh_CV.pdf"
              startIcon={<FileDownloadOutlinedIcon />}
              aria-label="Download CV (PDF)"
              sx={{
                minHeight: { xs: 50, sm: 54 },
                px: 2.5,
                borderRadius: 999,
                border: '2px solid',
                borderColor: 'primary.main',
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 600,
                fontSize: { xs: 14, sm: 15 },
                letterSpacing: '0.02em',
                textTransform: 'none',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  border: '2px solid',
                  borderColor: 'primary.main',
                  bgcolor: 'primary.main',
                  color: 'white',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                },
              }}
            >
              Download CV
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  </Box>
);

export default Hero; 